import { useRef, useState } from 'react';
import type { OutreachTemplateLintWarning } from '@discovr/types';
import type { ConfirmationRecipient } from './wizard/sendSummaryParts';
import { initialWizardState, type WizardState, type WizardTarget } from './wizard/wizardState';

/** The last drafting run: the template it used, a key over (template, creators), and that template's save warnings. */
export interface WizardRun {
  templateId: number;
  key: string;
  warnings?: OutreachTemplateLintWarning[];
  /** The operation the whole-batch drafting call returned: the `wizard_batch_id` of the approve. Unset until the call answers.
   *  `floor` is the newest initial draft's message id just before it started: only a draft above it belongs to this batch. */
  batch?: { id: string; startedAt: number; floor: number };
}

/** What the wizard keeps above itself so a remount resumes exactly where it was. */
export interface WizardSessionStore {
  /** P32 Q4: the creators whose drafts were ready at Message's Continue. By creator, not draft id, so a draft
   *  rewritten while the wizard is open stays in the batch (o-e2e2 N4). */
  batchEntryIds: string[] | null;
  setBatchEntryIds: (ids: string[] | null) => void;
  /** The last drafting run: the template it used and a key over (template, creators). Going Back and forward with
   *  nothing changed keeps the run, so a hand-edited draft is not regenerated by a mere Continue. */
  run: WizardRun | null;
  setRun: (run: WizardRun | null) => void;
  /** The wizard's own state (step, schedule, zone…), so a remount on Review keeps the chosen schedule.
   *  Read only on mount, so it lives in a ref: saving it (every keystroke) must not re-render the owner. */
  readSaved: () => WizardState | null;
  setSaved: (state: WizardState) => void;
  /** The approved creators, for the confirmation's avatar row and names. */
  recipients: Record<number, ConfirmationRecipient>;
  setRecipients: (recipients: Record<number, ConfirmationRecipient>) => void;
}

/** The bulk-send wizard's session, owned above the wizard so a running or
 *  just-finished batch survives it unmounting for an unrelated reason (e.g.
 *  the tab re-rendering another sub-view). Reset only by a new send or close. */
export function useSendWizardSession() {
  const [targets, setTargets] = useState<WizardTarget[] | null>(null);
  // The draft_initial operation; approveOperationId is the approve_drafts one.
  const [operationId, setOperationId] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);
  const [approveOperationId, setApproveOperationId] = useState<string | null>(null);
  const [batchEntryIds, setBatchEntryIds] = useState<string[] | null>(null);
  const saved = useRef<WizardState | null>(null);
  const [recipients, setRecipients] = useState<Record<number, ConfirmationRecipient>>({});
  const [run, setRun] = useState<WizardRun | null>(null);
  // Closed with drafts still being written: the session stays, only the panel is put away (the header badge brings it back).
  const [hidden, setHidden] = useState(false);

  const reset = () => {
    setRun(null);
    setHidden(false);
    setOperationId(null);
    setApproveOperationId(null);
    setSummary(null);
    setBatchEntryIds(null);
    saved.current = null;
    setRecipients({});
  };
  const open = (next: WizardTarget[]) => {
    reset();
    setTargets(next);
  };
  const close = () => {
    setTargets(null);
    reset();
  };
  /** Reopens a Bulk send Go still holds (after a reload, or another admin's) on its drafts step. */
  const openBatch = (next: WizardTarget[], batch: { id: string; startedAt: number; floor: number; templateId: number }) => {
    reset();
    setTargets(next);
    setOperationId(batch.id);
    setRun({ templateId: batch.templateId, key: `${batch.templateId}|${next.map((target) => target.entryId).join(',')}`, batch: { id: batch.id, startedAt: batch.startedAt, floor: batch.floor } });
    saved.current = initialWizardState(next, { templateId: batch.templateId, step: 'drafts' });
  };

  const readSaved = () => saved.current;
  const setSaved = (state: WizardState) => {
    saved.current = state;
  };
  const store: WizardSessionStore = { batchEntryIds, setBatchEntryIds, run, setRun, readSaved, setSaved, recipients, setRecipients };
  const hide = () => setHidden(true);
  const show = () => setHidden(false);
  return { targets, operationId, summary, approveOperationId, hidden, store, setOperationId, setSummary, setApproveOperationId, open, close, hide, show, openBatch, currentBatchId: run?.batch?.id ?? null };
}
